import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import NotificationService from '../services/notificationService';
import toast from 'react-hot-toast';
import { 
  Bell, 
  Send, 
  Users, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Search,
  Filter,
  RefreshCw,
  Trash2,
  BarChart3,
  Settings
} from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { formatIsoDateTimeDMY } from '../utils/dateLocale';

const AdminNotifications = () => {
  const { language, isRTL } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    targetType: '',
    startDate: '',
    endDate: ''
  });

  // Send notification form state
  const [sendForm, setSendForm] = useState({
    title: '',
    body: '',
    type: 'custom',
    targetType: 'all',
    selectedUsers: [],
    scheduledFor: '',
    requireInteraction: false,
    actions: []
  });

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      loadNotificationHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, filters]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [statsResult, templatesResult, schedulerResult] = await Promise.all([
        NotificationService.getDashboardStats(),
        NotificationService.getTemplates(),
        NotificationService.getSchedulerStatus()
      ]);

      if (statsResult.success) {
        setDashboardStats(statsResult.data);
      }

      if (templatesResult.success) {
        setTemplates(templatesResult.data);
      }

      if (schedulerResult.success) {
        setSchedulerStatus(schedulerResult.data);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationHistory = async () => {
    try {
      const params = {
        page: currentPage,
        limit: 10,
        ...filters
      };

      const result = await NotificationService.getHistory(params);
      if (result.success) {
        setNotificationHistory(result.data.notifications);
        setTotalPages(result.data.pagination.pages);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Error loading notification history:', error);
      toast.error('Failed to load notification history');
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const result = await NotificationService.searchUsers({ q: query, limit: 20 });
      if (result.success) {
        setSearchResults(result.data);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const handleSendNotification = async () => {
    if (!sendForm.title.trim() || !sendForm.body.trim()) {
      toast.error('Title and body are required');
      return;
    }

    if (sendForm.targetType === 'specific-users' && sendForm.selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    try {
      const notificationData = {
        title: sendForm.title,
        body: sendForm.body,
        type: sendForm.type,
        requireInteraction: sendForm.requireInteraction,
        actions: sendForm.actions
      };

      if (sendForm.scheduledFor) {
        notificationData.scheduledFor = new Date(sendForm.scheduledFor).toISOString();
      }

      let result;
      if (sendForm.targetType === 'all') {
        result = await NotificationService.sendToAll(notificationData);
      } else {
        result = await NotificationService.sendToUsers({
          ...notificationData,
          userIds: sendForm.selectedUsers.map(user => user._id)
        });
      }

      if (result.success) {
        toast.success('Notification sent successfully!');
        setSendForm({
          title: '',
          body: '',
          type: 'custom',
          targetType: 'all',
          selectedUsers: [],
          scheduledFor: '',
          requireInteraction: false,
          actions: []
        });
        setSelectedTemplate(null);
        loadInitialData(); // Refresh stats
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    }
  };

  const handleCancelNotification = async (id) => {
    try {
      const result = await NotificationService.cancelNotification(id);
      if (result.success) {
        toast.success('Notification cancelled successfully');
        loadNotificationHistory();
        loadInitialData();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Error cancelling notification:', error);
      toast.error('Failed to cancel notification');
    }
  };

  const applyTemplate = (template) => {
    setSendForm(prev => ({
      ...prev,
      title: template.title,
      body: template.body,
      type: template.type,
      requireInteraction: template.requireInteraction || false,
      actions: template.actions || []
    }));
    setSelectedTemplate(template);
  };

  const StatCard = ({ icon: Icon, title, value, color, bgColor }) => (
    <div style={{ 
      background: bgColor, 
      borderRadius: 16, 
      padding: 24, 
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.09)',
      border: '1px solid #f1f5f9'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ 
          width: 56, 
          height: 56, 
          background: color, 
          borderRadius: '50%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <Icon style={{ width: 24, height: 24, color: 'white' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#18181b', marginBottom: 4 }}>
            {value}
          </div>
          <div style={{ color: '#dc2626', fontSize: 14, fontWeight: 600 }}>
            {title}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div>
      {/* Statistics Cards */}
      {dashboardStats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24, marginBottom: 32 }}>
          <StatCard
            icon={Bell}
            title="Total Notifications"
            value={dashboardStats.overview.totalNotifications}
            color="linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)"
            bgColor="linear-gradient(135deg, #fafafa 0%, #fee2e2 100%)"
          />
          <StatCard
            icon={Send}
            title="Today's Notifications"
            value={dashboardStats.overview.todayNotifications}
            color="linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)"
            bgColor="linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
          />
          <StatCard
            icon={Clock}
            title="Scheduled"
            value={dashboardStats.overview.scheduledNotifications}
            color="linear-gradient(90deg, #f59e0b 0%, #d97706 100%)"
            bgColor="linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)"
          />
          <StatCard
            icon={TrendingUp}
            title="Success Rate"
            value={dashboardStats.overview.successRate}
            color="linear-gradient(90deg, #ef4444 0%, #dc2626 100%)"
            bgColor="linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)"
          />
        </div>
      )}

      {/* Scheduler Status */}
      {schedulerStatus && (
        <div style={{ 
          background: '#f8fafc', 
          borderRadius: 16, 
          padding: 24, 
          marginBottom: 32,
          border: '1px solid #fee2e2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Settings style={{ width: 24, height: 24, color: '#dc2626' }} />
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#18181b', margin: 0 }}>
              Scheduler Status
            </h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                background: schedulerStatus.isRunning ? '#10b981' : '#ef4444' 
              }} />
              <span style={{ color: '#18181b', fontWeight: 600 }}>
                {schedulerStatus.isRunning ? 'Running' : 'Stopped'}
              </span>
            </div>
            <div style={{ color: '#dc2626', fontSize: 14 }}>
              Pending: {schedulerStatus.pendingNotifications}
            </div>
          </div>
        </div>
      )}

      {/* Recent Notifications */}
      {dashboardStats?.recentNotifications && (
        <div style={{ 
          background: '#f8fafc', 
          borderRadius: 16, 
          padding: 24,
          border: '1px solid #fee2e2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <BarChart3 style={{ width: 24, height: 24, color: '#dc2626' }} />
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#18181b', margin: 0 }}>
              Recent Notifications
            </h3>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {dashboardStats.recentNotifications.map((notification) => (
              <div key={notification._id} style={{ 
                background: 'white', 
                borderRadius: 12, 
                padding: 16, 
                border: '1px solid #fee2e2'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#18181b', marginBottom: 4 }}>
                      {notification.title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 14, color: '#dc2626' }}>
                      <span>Type: {notification.type}</span>
                      <span>Target: {notification.targetType}</span>
                      <span>Sent: {notification.deliveryStats.successful}</span>
                    </div>
                  </div>
                  <div style={{ 
                    background: notification.status === 'sent' ? '#dcfce7' : 
                               notification.status === 'failed' ? '#fef2f2' : '#fef3c7',
                    color: notification.status === 'sent' ? '#166534' : 
                           notification.status === 'failed' ? '#dc2626' : '#92400e',
                    padding: '4px 12px', 
                    borderRadius: 8, 
                    fontSize: 12, 
                    fontWeight: 600 
                  }}>
                    {notification.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderSendNotification = () => (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ 
        background: 'white', 
        borderRadius: 16, 
        padding: 32,
        border: '1px solid #fee2e2'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Send style={{ width: 24, height: 24, color: '#dc2626' }} />
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#18181b', margin: 0 }}>
            Send Notification
          </h3>
        </div>

        {/* Template Selection */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', color: '#18181b', fontWeight: 600, marginBottom: 8 }}>
            Use Template (Optional)
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => applyTemplate(template)}
                style={{ 
                  background: selectedTemplate?.id === template.id ? '#dc2626' : '#f8fafc',
                  color: selectedTemplate?.id === template.id ? 'white' : '#18181b',
                  border: '1px solid #fee2e2',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {template.name}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', color: '#18181b', fontWeight: 600, marginBottom: 8 }}>
            Title *
          </label>
          <input
            type="text"
            value={sendForm.title}
            onChange={(e) => setSendForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Enter notification title..."
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: 16,
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box'
            }}
            maxLength={100}
          />
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {sendForm.title.length}/100 characters
          </div>
        </div>

        {/* Body */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', color: '#18181b', fontWeight: 600, marginBottom: 8 }}>
            Message *
          </label>
          <textarea
            value={sendForm.body}
            onChange={(e) => setSendForm(prev => ({ ...prev, body: e.target.value }))}
            placeholder="Enter notification message..."
            rows={4}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: 16,
              outline: 'none',
              transition: 'border-color 0.2s',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
            maxLength={500}
          />
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {sendForm.body.length}/500 characters
          </div>
        </div>

        {/* Type and Target */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', color: '#18181b', fontWeight: 600, marginBottom: 8 }}>
              Type
            </label>
            <select
              value={sendForm.type}
              onChange={(e) => setSendForm(prev => ({ ...prev, type: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 16,
                outline: 'none',
                background: 'white',
                boxSizing: 'border-box'
              }}
            >
              <option value="custom">Custom</option>
              <option value="system">System</option>
              <option value="announcement">Announcement</option>
              <option value="promotion">Promotion</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', color: '#18181b', fontWeight: 600, marginBottom: 8 }}>
              Send To
            </label>
            <select
              value={sendForm.targetType}
              onChange={(e) => setSendForm(prev => ({ ...prev, targetType: e.target.value, selectedUsers: [] }))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 16,
                outline: 'none',
                background: 'white',
                boxSizing: 'border-box'
              }}
            >
              <option value="all">All Users</option>
              <option value="specific-users">Specific Users</option>
            </select>
          </div>
        </div>

        {/* User Selection */}
        {sendForm.targetType === 'specific-users' && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', color: '#18181b', fontWeight: 600, marginBottom: 8 }}>
              Select Users
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchUsers(e.target.value);
                }}
                placeholder="Search users by name or email..."
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 44px',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <Search style={{ 
                position: 'absolute', 
                left: 12, 
                top: '50%', 
                transform: 'translateY(-50%)', 
                width: 20, 
                height: 20, 
                color: '#6b7280' 
              }} />
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div style={{ 
                maxHeight: 200, 
                overflowY: 'auto', 
                border: '1px solid #d1d5db', 
                borderRadius: 8, 
                marginTop: 8 
              }}>
                {searchResults.map((user) => (
                  <div
                    key={user._id}
                    onClick={() => {
                      if (!sendForm.selectedUsers.find(u => u._id === user._id)) {
                        setSendForm(prev => ({ 
                          ...prev, 
                          selectedUsers: [...prev.selectedUsers, user] 
                        }));
                      }
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      background: 'white',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <div style={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: '50%', 
                      background: '#dc2626', 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 600
                    }}>
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#18181b' }}>{user.username}</div>
                      <div style={{ fontSize: 14, color: '#6b7280' }}>{user.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected Users */}
            {sendForm.selectedUsers.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>
                  Selected Users ({sendForm.selectedUsers.length}):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {sendForm.selectedUsers.map((user) => (
                    <div
                      key={user._id}
                      style={{
                        background: '#fee2e2',
                        color: '#18181b',
                        padding: '4px 12px',
                        borderRadius: 16,
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span>{user.username}</span>
                      <button
                        onClick={() => setSendForm(prev => ({ 
                          ...prev, 
                          selectedUsers: prev.selectedUsers.filter(u => u._id !== user._id) 
                        }))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <XCircle style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Schedule */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', color: '#18181b', fontWeight: 600, marginBottom: 8 }}>
            Schedule (Optional)
          </label>
          <input
            type="datetime-local"
            value={sendForm.scheduledFor}
            onChange={(e) => setSendForm(prev => ({ ...prev, scheduledFor: e.target.value }))}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: 16,
              outline: 'none',
              boxSizing: 'border-box'
            }}
            min={new Date().toISOString().slice(0, 16)}
          />
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Leave empty to send immediately
          </div>
        </div>

        {/* Options */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={sendForm.requireInteraction}
              onChange={(e) => setSendForm(prev => ({ ...prev, requireInteraction: e.target.checked }))}
            />
            <span style={{ color: '#18181b', fontWeight: 600 }}>Require User Interaction</span>
          </label>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, marginLeft: 24 }}>
            Notification will stay visible until user interacts with it
          </div>
        </div>

        {/* Send Button */}
        <button
          onClick={handleSendNotification}
          disabled={!sendForm.title.trim() || !sendForm.body.trim()}
          style={{
            width: '100%',
            background: !sendForm.title.trim() || !sendForm.body.trim() ? '#d1d5db' : 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)',
            color: 'white',
            border: 'none',
            borderRadius: 12,
            padding: '16px 24px',
            fontSize: 16,
            fontWeight: 600,
            cursor: !sendForm.title.trim() || !sendForm.body.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            transition: 'all 0.2s'
          }}
        >
          <Send style={{ width: 20, height: 20 }} />
          {sendForm.scheduledFor ? 'Schedule Notification' : 'Send Notification'}
        </button>
      </div>
    </div>
  );

  const renderHistory = () => (
    <div>
      {/* Filters */}
      <div style={{ 
        background: 'white', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #fee2e2'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Filter style={{ width: 20, height: 20, color: '#dc2626' }} />
          <span style={{ color: '#18181b', fontWeight: 600 }}>Filters</span>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: 14,
              outline: 'none'
            }}
          >
            <option value="">All Status</option>
            <option value="sent">Sent</option>
            <option value="scheduled">Scheduled</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: 14,
              outline: 'none'
            }}
          >
            <option value="">All Types</option>
            <option value="custom">Custom</option>
            <option value="system">System</option>
            <option value="announcement">Announcement</option>
            <option value="promotion">Promotion</option>
          </select>

          <select
            value={filters.targetType}
            onChange={(e) => setFilters(prev => ({ ...prev, targetType: e.target.value }))}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: 14,
              outline: 'none'
            }}
          >
            <option value="">All Targets</option>
            <option value="all">All Users</option>
            <option value="specific-users">Specific Users</option>
          </select>

          <button
            onClick={loadNotificationHistory}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <RefreshCw style={{ width: 16, height: 16 }} />
            Apply Filters
          </button>
        </div>
      </div>

      {/* History List */}
      <div style={{ 
        background: 'white', 
        borderRadius: 16, 
        padding: 24,
        border: '1px solid #fee2e2'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Clock style={{ width: 24, height: 24, color: '#dc2626' }} />
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#18181b', margin: 0 }}>
            Notification History
          </h3>
        </div>

        {notificationHistory.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {notificationHistory.map((notification) => (
              <div key={notification._id} style={{ 
                border: '1px solid #fee2e2',
                borderRadius: 12,
                padding: 20
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#18181b', marginBottom: 4 }}>
                      {notification.title}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 14, marginBottom: 8 }}>
                      {notification.body}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: '#dc2626' }}>
                      <span>Type: {notification.type}</span>
                      <span>Target: {notification.targetType}</span>
                      <span>By: {notification.sentBy?.username}</span>
                      <span>Created: {formatIsoDateTimeDMY(notification.createdAt)}</span>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ 
                      background: notification.status === 'sent' ? '#dcfce7' : 
                                 notification.status === 'failed' ? '#fef2f2' : 
                                 notification.status === 'scheduled' ? '#fef3c7' : '#f3f4f6',
                      color: notification.status === 'sent' ? '#166534' : 
                             notification.status === 'failed' ? '#dc2626' : 
                             notification.status === 'scheduled' ? '#92400e' : '#6b7280',
                      padding: '4px 12px', 
                      borderRadius: 16, 
                      fontSize: 12, 
                      fontWeight: 600 
                    }}>
                      {notification.status}
                    </div>
                    
                    {notification.status === 'scheduled' && (
                      <button
                        onClick={() => handleCancelNotification(notification._id)}
                        style={{
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: 8,
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <Trash2 style={{ width: 14, height: 14 }} />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 24, fontSize: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#10b981' }}>
                    <CheckCircle style={{ width: 16, height: 16 }} />
                    <span>Sent: {notification.deliveryStats.successful}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444' }}>
                    <XCircle style={{ width: 16, height: 16 }} />
                    <span>Failed: {notification.deliveryStats.failed}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626' }}>
                    <Users style={{ width: 16, height: 16 }} />
                    <span>Total: {notification.deliveryStats.totalTargeted}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#6b7280' }}>
            <Bell style={{ width: 48, height: 48, color: '#d1d5db', marginBottom: 16 }} />
            <div style={{ fontSize: 16 }}>No notifications found</div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 24 }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                background: currentPage === 1 ? '#f3f4f6' : '#dc2626',
                color: currentPage === 1 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            
            <span style={{ color: '#18181b', fontSize: 14 }}>
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                background: currentPage === totalPages ? '#f3f4f6' : '#dc2626',
                color: currentPage === totalPages ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout
        title={language === 'en' ? 'Notifications' : 'התראות'}
        subtitle={
          language === 'en'
            ? 'Send and manage push notifications'
            : 'שליחה וניהול התראות'
        }
        loading
      />
    );
  }

  return (
    <AdminLayout
      title={language === 'en' ? 'Notifications' : 'התראות'}
      subtitle={
        language === 'en'
          ? 'Send and manage notifications to users'
          : 'שליחה וניהול התראות למשתמשים'
      }
    >
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md shadow-slate-900/5">
        {/* Tab Navigation */}
        <div
          className="border-b border-slate-200 px-4 sm:px-6"
          style={{ direction: isRTL ? 'rtl' : 'ltr' }}
        >
          <div style={{ display: 'flex', gap: 0 }}>
            {[
              { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { key: 'send', label: 'Send Notification', icon: Send },
              { key: 'history', label: 'History', icon: Clock }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    background: activeTab === tab.key ? '#dc2626' : 'transparent',
                    color: activeTab === tab.key ? 'white' : '#6b7280',
                    border: 'none',
                    padding: '16px 24px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    borderRadius: '8px 8px 0 0',
                    transition: 'all 0.2s'
                  }}
                >
                  <Icon style={{ width: 18, height: 18 }} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div style={{ 
          background: 'white', 
          borderRadius: '0 0 16px 16px', 
          padding: 32,
          border: '1px solid #fee2e2',
          borderTop: 'none',
          minHeight: 400
        }}>
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'send' && renderSendNotification()}
          {activeTab === 'history' && renderHistory()}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNotifications;
